import React from 'react';
import { CheckCircle2, Download, Megaphone, Send, TestTube2, Users } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';
import { downloadPreparedFile } from '../utils/downloadFile';

const getApiPath = (path) => {
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const response = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.error || `Request failed: ${response.status}`);
  return data;
};

const countryName = (country) => (country === 'KZ' ? 'Казахстан' : 'Кыргызстан');

const ManagerBroadcastPage = () => {
  const { initData, tg } = useTelegram();
  const [info, setInfo] = React.useState(null);
  const [text, setText] = React.useState('');
  const [testToken, setTestToken] = React.useState('');
  const [job, setJob] = React.useState(null);
  const [confirming, setConfirming] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');

  const initDataRaw = initData?.initData;

  const loadInfo = React.useCallback(async () => {
    if (!initDataRaw) return;
    setError('');
    try {
      const result = await apiPost('/api/manager/broadcast/info', { initData: initDataRaw });
      setInfo(result);
      if (result?.activeJob) setJob(result.activeJob);
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить данные рассылки');
    }
  }, [initDataRaw]);

  React.useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  React.useEffect(() => {
    if (!initDataRaw || !job?.id || job.status !== 'running') return undefined;
    const timer = window.setInterval(async () => {
      try {
        const result = await apiPost('/api/manager/broadcast/status', {
          initData: initDataRaw,
          jobId: job.id,
        });
        setJob(result.job);
        if (result.job?.status === 'completed') {
          setNotice('Рассылка завершена');
          await loadInfo();
        }
      } catch (e) {
        setError(e?.message || 'Не удалось получить состояние рассылки');
      }
    }, 1500);
    return () => window.clearInterval(timer);
  }, [initDataRaw, job?.id, job?.status, loadInfo]);

  const updateText = (value) => {
    setText(value.slice(0, 4000));
    setTestToken('');
    setConfirming(false);
    setNotice('');
  };

  const sendTest = async () => {
    if (!initDataRaw || !text.trim()) return;
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const result = await apiPost('/api/manager/broadcast/test', {
        initData: initDataRaw,
        text,
      });
      setTestToken(result.testToken || '');
      setNotice('Тест отправлен вам в личные сообщения. Проверьте текст перед рассылкой.');
    } catch (e) {
      setError(e?.message || 'Не удалось отправить тест');
    } finally {
      setLoading(false);
    }
  };

  const startBroadcast = async () => {
    if (!initDataRaw || !testToken) return;
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const result = await apiPost('/api/manager/broadcast/send', {
        initData: initDataRaw,
        text,
        testToken,
        confirmed: true,
      });
      setJob(result.job);
      setTestToken('');
      setConfirming(false);
      setNotice('Рассылка запущена. Страницу можно оставить открытой для просмотра прогресса.');
    } catch (e) {
      setError(e?.message || 'Не удалось запустить рассылку');
    } finally {
      setLoading(false);
    }
  };

  const downloadIssues = async () => {
    if (!initDataRaw || !job?.id) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(getApiPath('/api/manager/broadcast/issues/export'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ initData: initDataRaw, jobId: job.id, prepareDownload: true }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || 'Не удалось сформировать Excel');
      }
      const prepared = await response.json();
      await downloadPreparedFile(tg, prepared, `broadcast_not_delivered_${job.country || 'all'}.xlsx`);
    } catch (e) {
      setError(e?.message || 'Не удалось скачать список');
    } finally {
      setLoading(false);
    }
  };

  const running = job?.status === 'running';
  const progress = job?.total ? Math.round((Number(job.processed || 0) / Number(job.total)) * 100) : 0;

  return (
    <div className="min-h-screen bg-bg text-text-primary pb-20">
      <div className="mx-auto max-w-2xl px-4 pt-6">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-neon-yellow/25 bg-neon-yellow/10 text-neon-yellow">
            <Megaphone size={21} />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Рассылка клиентам</h1>
            <p className="mt-1 text-sm text-text-secondary">Обычное текстовое сообщение всем клиентам вашей страны.</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-stroke bg-surface p-4">
            <div className="text-xs text-text-muted">Страна</div>
            <div className="mt-1 font-semibold">{info ? countryName(info.country) : '...'}</div>
          </div>
          <div className="rounded-2xl border border-stroke bg-surface p-4">
            <div className="flex items-center gap-2 text-xs text-text-muted"><Users size={14} /> Получателей</div>
            <div className="mt-1 text-lg font-semibold">{info?.recipientCount ?? '...'}</div>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-stroke bg-surface p-4">
          <label htmlFor="broadcast-text" className="text-sm font-semibold">Текст сообщения</label>
          <textarea
            id="broadcast-text"
            value={text}
            onChange={(event) => updateText(event.target.value)}
            disabled={running}
            rows={9}
            placeholder="Напишите сообщение об акции, режиме работы или важной новости..."
            className="mt-3 w-full resize-y rounded-2xl border border-stroke bg-[#161616] px-4 py-3 text-[16px] leading-relaxed text-white outline-none placeholder:text-white/35 focus:border-white/25 disabled:opacity-60"
          />
          <div className="mt-2 flex items-center justify-between gap-3 text-xs text-text-muted">
            <span>Переносы строк и эмодзи сохранятся.</span>
            <span>{text.length}/4000</span>
          </div>

          <button
            type="button"
            onClick={sendTest}
            disabled={loading || running || !text.trim()}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-neon-blue/30 bg-neon-blue/10 py-3 text-sm font-semibold text-neon-blue transition hover:bg-neon-blue/15 disabled:opacity-50"
          >
            <TestTube2 size={17} /> Отправить тест себе
          </button>

          {testToken ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={loading || running}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-neon-yellow py-3 text-sm font-semibold text-black transition hover:brightness-110 disabled:opacity-50"
            >
              <Send size={17} /> Запустить рассылку
            </button>
          ) : null}
        </div>

        {confirming ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="broadcast-confirm-title">
            <div className="w-full max-w-md rounded-3xl border border-amber-400/30 bg-[#171717] p-5 shadow-2xl">
              <div id="broadcast-confirm-title" className="text-lg font-semibold text-amber-100">Подтвердите массовую рассылку</div>
              <div className="mt-3 rounded-2xl border border-stroke bg-white/5 p-4 text-sm leading-relaxed text-text-secondary">
                <div>Страна: <span className="font-semibold text-text-primary">{countryName(info?.country)}</span></div>
                <div className="mt-1">Получателей: <span className="font-semibold text-text-primary">{info?.recipientCount ?? 0}</span></div>
              </div>
              <div className="mt-3 text-xs leading-relaxed text-amber-50/75">
                После запуска остановить уже отправленные сообщения нельзя.
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setConfirming(false)} disabled={loading} className="rounded-2xl border border-stroke bg-white/5 py-3 text-sm font-semibold disabled:opacity-50">Отмена</button>
                <button type="button" onClick={startBroadcast} disabled={loading} className="rounded-2xl bg-neon-yellow py-3 text-sm font-semibold text-black disabled:opacity-50">{loading ? 'Запуск...' : 'Да, отправить всем'}</button>
              </div>
            </div>
          </div>
        ) : null}

        {job ? (
          <div className="mt-4 rounded-2xl border border-stroke bg-surface p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="font-semibold">{running ? 'Рассылка выполняется' : job.status === 'completed' ? 'Рассылка завершена' : 'Ошибка рассылки'}</div>
              <div className="text-sm font-semibold text-neon-yellow">{running ? `${progress}%` : ''}</div>
            </div>
            {running ? <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-neon-yellow transition-all" style={{ width: `${progress}%` }} /></div> : null}
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              <div className="rounded-xl border border-stroke bg-white/3 p-3"><div className="text-text-muted">Всего</div><div className="mt-1 font-semibold">{job.total || 0}</div></div>
              <div className="rounded-xl border border-stroke bg-white/3 p-3"><div className="text-text-muted">Отправлено</div><div className="mt-1 font-semibold text-emerald-300">{job.sent || 0}</div></div>
              <div className="rounded-xl border border-stroke bg-white/3 p-3"><div className="text-text-muted">Пропущено</div><div className="mt-1 font-semibold">{job.skipped || 0}</div></div>
              <div className="rounded-xl border border-stroke bg-white/3 p-3"><div className="text-text-muted">Ошибки</div><div className="mt-1 font-semibold text-red-300">{job.failed || 0}</div></div>
            </div>
            {!running && (Number(job.skipped || 0) + Number(job.failed || 0) > 0) ? (
              <>
                <button
                  type="button"
                  onClick={downloadIssues}
                  disabled={loading}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 py-3 text-sm font-semibold transition hover:bg-white/10 disabled:opacity-50"
                >
                  <Download size={17} /> Скачать неотправленные
                </button>
                <div className="mt-2 text-xs leading-relaxed text-text-muted">
                  Excel содержит причину, Telegram ID, ФИО, телефон, username и страну. Список доступен в течение часа или до перезапуска backend.
                </div>
              </>
            ) : null}
            {job.error ? <div className="mt-3 text-sm text-red-300">{job.error}</div> : null}
          </div>
        ) : null}

        {notice ? <div className="mt-4 flex items-start gap-2 rounded-2xl border border-emerald-400/25 bg-emerald-400/10 p-4 text-sm text-emerald-100"><CheckCircle2 size={18} className="mt-0.5 shrink-0" />{notice}</div> : null}
        {error ? <div className="mt-4 rounded-2xl border border-red-400/25 bg-red-400/10 p-4 text-sm text-red-200">{error}</div> : null}
      </div>
    </div>
  );
};

export default ManagerBroadcastPage;
